import React, { useEffect, useState } from "react";
import { Users, Loader2, UserPlus, Trash2, Mail, Copy, CreditCard } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { MemberAvatar } from "@/components/MemberAvatar";

const ROLE_COLORS = {
  owner: "bg-blue-100 text-blue-700",
  admin: "bg-violet-100 text-violet-700",
  member: "bg-slate-100 text-slate-700",
  viewer: "bg-amber-100 text-amber-700",
};

const TeamSettings = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [inviting, setInviting] = useState(false);
  const [lastLink, setLastLink] = useState("");
  const [seatQty, setSeatQty] = useState(0);
  const [savingSeats, setSavingSeats] = useState(false);

  const load = async () => {
    try {
      const res = await api.get("/team");
      setData(res.data);
      setSeatQty(res.data.seats?.extra ?? 0);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const invite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    setLastLink("");
    try {
      const { data: res } = await api.post("/team/invite", { email: inviteEmail.trim(), role: inviteRole, origin_url: window.location.origin });
      setLastLink(res.invite_link);
      setInviteEmail("");
      toast.success(`Invite sent to ${res.email}`);
      await load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setInviting(false);
    }
  };

  const changeRole = async (memberId, role) => {
    try {
      await api.patch(`/team/member/${memberId}`, { role });
      toast.success("Role updated");
      await load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const removeMember = async (memberId) => {
    try {
      await api.delete(`/team/member/${memberId}`);
      toast.success("Team member removed");
      await load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const revokeInvite = async (id) => {
    try {
      await api.delete(`/team/invite/${id}`);
      toast.success("Invite revoked");
      await load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const saveSeats = async () => {
    setSavingSeats(true);
    try {
      const { data: res } = await api.post("/billing/seats", { quantity: Number(seatQty) });
      toast.success(`You now have ${res.seat_limit} seats`);
      await load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSavingSeats(false); }
  };

  const copyLink = () => { navigator.clipboard.writeText(lastLink); toast.success("Invite link copied"); };

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-6 flex items-center gap-2 text-slate-400" data-testid="team-section">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading your team…
      </div>
    );
  }
  if (!data) return null;

  const { members, invitations, seats, can_manage, is_owner } = data;
  const seatsFull = seats.used >= seats.limit;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="team-section">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2">
          <Users className="h-5 w-5 text-blue-600" /> Team
        </h2>
        <span className="text-sm text-slate-500" data-testid="seat-usage">
          <span className={`font-semibold ${seatsFull ? "text-amber-600" : "text-slate-800"}`}>{seats.used}</span> of {seats.limit} seats used
        </span>
      </div>
      <p className="text-sm text-slate-500 mt-0.5 mb-4">Invite your team. Pending invites reserve a seat. Roles control what each person can do.</p>

      {/* Invite form */}
      {can_manage && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 mb-5" data-testid="invite-block">
          <div className="grid sm:grid-cols-[1fr_auto_auto] gap-3 items-end">
            <div>
              <Label className="text-xs">Email address</Label>
              <Input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="mate@company.co.uk" data-testid="invite-email-input" className="mt-1 bg-white" />
            </div>
            <div>
              <Label className="text-xs">Role</Label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger className="mt-1 w-[130px] bg-white" data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin" data-testid="invite-role-admin">Admin</SelectItem>
                  <SelectItem value="member" data-testid="invite-role-member">Member</SelectItem>
                  <SelectItem value="viewer" data-testid="invite-role-viewer">Viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={invite} disabled={inviting || !inviteEmail.trim()} data-testid="send-invite-btn" className="bg-blue-600 hover:bg-blue-700">
              {inviting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <UserPlus className="h-4 w-4 mr-1.5" />} Invite
            </Button>
          </div>
          {lastLink && (
            <div className="mt-3 flex items-center gap-2 text-xs bg-white border border-slate-200 rounded-md px-3 py-2" data-testid="invite-link-box">
              <span className="text-slate-500 truncate flex-1">{lastLink}</span>
              <button onClick={copyLink} className="text-blue-600 hover:text-blue-700 flex items-center gap-1 shrink-0" data-testid="copy-invite-link"><Copy className="h-3.5 w-3.5" /> Copy</button>
            </div>
          )}
          {seatsFull && <p className="text-xs text-amber-600 mt-2">You've used all your seats. {is_owner ? "Add extra seats below or upgrade your plan." : "Ask the workspace owner to add seats."}</p>}
        </div>
      )}

      {/* Members */}
      <div className="space-y-2" data-testid="members-list">
        {members.map((m) => (
          <div key={m.id} className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0" data-testid={`member-row-${m.email}`}>
            <MemberAvatar name={m.name} email={m.email} picture={m.picture} size="md" testId={`member-avatar-${m.email}`} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-800 truncate">{m.name} {m.is_you && <span className="text-slate-400 font-normal">(you)</span>}</p>
              <p className="text-xs text-slate-400 truncate">{m.email}</p>
            </div>
            {can_manage && !m.is_owner && !m.is_you ? (
              <Select value={m.role} onValueChange={(v) => changeRole(m.id, v)}>
                <SelectTrigger className="w-[110px] h-8 text-xs" data-testid={`role-select-${m.email}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="viewer">Viewer</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <Badge className={`${ROLE_COLORS[m.role] || ROLE_COLORS.member} border-0 capitalize`} data-testid={`role-badge-${m.email}`}>{m.role}</Badge>
            )}
            {can_manage && !m.is_owner && !m.is_you && (
              <button onClick={() => removeMember(m.id)} className="text-slate-300 hover:text-red-500 shrink-0" data-testid={`remove-member-${m.email}`}><Trash2 className="h-4 w-4" /></button>
            )}
          </div>
        ))}
      </div>

      {/* Pending invites */}
      {invitations.length > 0 && (
        <div className="mt-5" data-testid="pending-invites">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Pending invites</p>
          <div className="space-y-2">
            {invitations.map((i) => (
              <div key={i.id} className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0" data-testid={`invite-row-${i.email}`}>
                <div className="h-9 w-9 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center shrink-0"><Mail className="h-4 w-4" /></div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-700 truncate">{i.email}</p>
                  <p className="text-xs text-slate-400">Pending · {i.role}</p>
                </div>
                <Badge className={`${ROLE_COLORS[i.role] || ROLE_COLORS.member} border-0 capitalize`}>{i.role}</Badge>
                {can_manage && (
                  <button onClick={() => revokeInvite(i.id)} className="text-slate-300 hover:text-red-500 shrink-0" data-testid={`revoke-invite-${i.email}`}><Trash2 className="h-4 w-4" /></button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Extra seats (owner only) */}
      {is_owner && (
        <div className="mt-6 pt-5 border-t border-slate-200" data-testid="extra-seats-block">
          <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2"><CreditCard className="h-4 w-4 text-slate-400" /> Extra seats</h3>
          {seats.plan === "free" ? (
            <p className="text-sm text-slate-500 mt-1">
              Your Free plan includes {seats.base} seat. <Link to="/app/account" className="text-blue-600 font-medium">Upgrade to a paid plan</Link> to add extra seats.
            </p>
          ) : (
            <>
              <p className="text-sm text-slate-500 mt-1">Your <span className="capitalize">{seats.plan}</span> plan includes {seats.base} seats. Add more at {seats.billing_interval === "annual" ? `£${seats.seat_price_annual}/seat/year` : `£${seats.seat_price}/seat/month`} (excl. VAT). Charged immediately, pro-rated.</p>
              <div className="flex items-end gap-3 mt-3">
                <div>
                  <Label className="text-xs">Extra seats</Label>
                  <Input type="number" min={0} max={200} value={seatQty} onChange={(e) => setSeatQty(e.target.value)} data-testid="extra-seats-input" className="mt-1 w-28" />
                </div>
                <Button onClick={saveSeats} disabled={savingSeats || Number(seatQty) === seats.extra} data-testid="save-seats-btn" className="bg-blue-600 hover:bg-blue-700">
                  {savingSeats ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null} Update seats
                </Button>
                <span className="text-sm text-slate-500 pb-2">= {seats.base + Number(seatQty || 0)} total seats</span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default TeamSettings;
